<script>
import baseMixin from '@/mixins/baseMixin'
import CommentModel from '@/models/comments/commentModel'
import timesheetMixin from '@/mixins/timesheetMixin'
import { ResolveOperations } from '@/constants/disputes/ResolveOperations'
import { mapGetters } from 'vuex'
import DisputeTimesheetDetailsTimeEntryCommentModel from '@/models/disputes/disputeTimesheetDetailsTimeEntryCommentModel.js'
import CommentBase from './comment-base.vue'
import CommentBaseText from './comment-base-text.vue'
import CommentDisputeHistoryActionBtn from './comment-dispute-history-action-btn.vue'

export default {
  name: 'CommentDisputeHistoryCandidateTimeEntry',
  components: {
    CommentBase,
    CommentBaseText,
    CommentDisputeHistoryActionBtn,
  },
  mixins: [baseMixin, timesheetMixin],
  props: {
    comment: {
      type: CommentModel,
      required: true,
    },
  },
  computed: {
    ...mapGetters({
      resolveStatus: 'disputes/resolveStatus',
    }),
    serialisedComment() {
      return this.parseJson(
        this.comment.commentText,
        {},
        DisputeTimesheetDetailsTimeEntryCommentModel
      )
    },
    isSelected() {
      return this.resolveStatus === ResolveOperations.TIME_ENTRY
    },
    buttonText() {
      if (this.isSelected) return this.$t('general.selected')
      return this.$t('comment.disputeHistory.useThisBtnText')
    },
    commentTitle() {
      // Incomplete time entry
      if (!this.serialisedComment.endTime)
        return this.$t('comment.disputeHistory.timeEntry.partialTitle')

      return this.$t('comment.disputeHistory.timeEntry.commentTitle')
    },
    startTimeText() {
      if (
        this.serialisedComment.bookingStartTime ===
        this.serialisedComment.startTime
      )
        return this.renderLocalTime(this.serialisedComment.bookingStartTime)

      return `${this.renderLocalTime(
        this.serialisedComment.bookingStartTime
      )} ➜ ${this.renderLocalTime(this.serialisedComment.startTime)}`
    },
    endTimeText() {
      if (
        this.serialisedComment.bookingEndTime === this.serialisedComment.endTime
      )
        return this.renderLocalTime(this.serialisedComment.bookingEndTime)

      if (!this.serialisedComment.endTime)
        return this.$t(
          'comment.disputeHistory.timeEntry.noClockOutTimeRecorded'
        )

      return `${this.renderLocalTime(
        this.serialisedComment.bookingEndTime
      )} ➜ ${this.renderLocalTime(this.serialisedComment.endTime)}`
    },
    breakMinutesText() {
      const { mealBreakMinutes, bookingMealBreakMinutes } =
        this.serialisedComment

      if (mealBreakMinutes == null && bookingMealBreakMinutes == null)
        return null

      const text = this.$t('comment.disputeHistory.original.breakMins', [
        mealBreakMinutes ?? bookingMealBreakMinutes,
      ])

      return mealBreakMinutes == null
        ? text + this.$t('comment.disputeHistory.timeEntry.noMealBreakRecorded')
        : text
    },
    isPartialTimeEntryInvalid() {
      return this.isPartialTimeEntryClockInAfterScheduledEndTime(
        {
          startTime: this.serialisedComment.startTime,
          endTime: this.serialisedComment.endTime,
        },
        this.serialisedComment.bookingEndTime
      )
    },
  },
  methods: {
    updateResolveStatus() {
      this.$store.commit('disputes/SET_FLYOUT', {
        key: 'resolveStatus',
        value: ResolveOperations.TIME_ENTRY,
      })
    },
  },
}
</script>

<template>
  <CommentBase
    :added-by="comment.addedBy"
    :subtitle="comment.getDateCreatedTimestamp()"
    :active="isSelected"
  >
    <div class="d-flex">
      <div class="flex-grow-1">
        <CommentBaseText
          :class="{
            'opacity-8': isPartialTimeEntryInvalid,
          }"
          >{{ commentTitle }}</CommentBaseText
        >

        <div
          class="my-2"
          :class="{
            'opacity-5': isPartialTimeEntryInvalid,
          }"
        >
          <CommentBaseText
            ><span class="font-weight-bold"
              >{{ $t('comment.disputeHistory.startTimeLabel') }}:</span
            >
            {{ startTimeText }}</CommentBaseText
          >
          <CommentBaseText
            v-if="endTimeText"
            :class="{ 'font-weight-bold ': !serialisedComment.endTime }"
            ><span v-if="serialisedComment.endTime" class="font-weight-bold"
              >{{ $t('comment.disputeHistory.endTimeLabel') }}:</span
            >
            {{ endTimeText }}</CommentBaseText
          >
          <CommentBaseText
            v-if="breakMinutesText"
            :class="{
              'font-weight-bold ': !serialisedComment.mealBreakMinutes,
            }"
            >{{ breakMinutesText }}</CommentBaseText
          >
        </div>
        <p
          v-if="isPartialTimeEntryInvalid"
          class="error--text font-weight-medium text-body-2"
        >
          <v-icon size="20" color="error" class="mb-1" left
            >mdi-alert-circle-outline</v-icon
          >
          {{
            $t(
              'comment.disputeHistory.timeEntry.partialTimeEntryClockInAfterScheduledEndTime'
            )
          }}</p
        >
      </div>

      <div
        class="dispute-history-comment-actions d-flex flex-column justify-space-between align-end ml-4"
      >
        <v-chip
          v-if="comment.isPriorityTimesheet"
          color="primary darken-2"
          small
        >
          <v-icon left small>mdi-clock-outline</v-icon>
          {{ $t('dispute.history.priorityTimesheetBadge') }}
        </v-chip>
        <div v-else></div>

        <CommentDisputeHistoryActionBtn
          :selected="isSelected"
          :disabled="isPartialTimeEntryInvalid"
          :text="buttonText"
          @click="updateResolveStatus"
        />
        <div></div>
      </div>
    </div>
  </CommentBase>
</template>
